
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * JournalBusinessRecord struct
 */
class JournalBusinessRecord {
  /**
   * Initialize struct
   * @param {Date=} time
   * @param {!string=} message
   * @constructor
   */
  constructor (time = null, message = '') {
    this.Time = time
    this.Message = message
  }

  /**
   * Copy struct (shallow copy)
   * @this {!JournalBusinessRecord}
   * @param {!JournalBusinessRecord} other Other struct
   * @returns {!JournalBusinessRecord} This struct
   */
  copy (other) {
    if (other.Time != null) {
      if (other.Time instanceof Date) {
        this.Time = new Date(other.Time.getTime())
      } else {
        this.Time = new Date(Math.round(other.Time / 1000000))
      }
    } else {
      this.Time = null
    }
    if (other.Message != null) {
      this.Message = other.Message
    } else {
      this.Message = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!JournalBusinessRecord}
   * @returns {!JournalBusinessRecord} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new JournalBusinessRecordModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new JournalBusinessRecordModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!JournalBusinessRecord}
   * @param {!JournalBusinessRecord} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof JournalBusinessRecord)) {
      throw new TypeError('Instance of JournalBusinessRecord is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!JournalBusinessRecord}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Time: ((this.Time != null) ? (this.Time.getTime() * 1000000) : null),
      Message: ((this.Message != null) ? this.Message : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return JournalBusinessRecord.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!JournalBusinessRecord} other Object value
   * @returns {!JournalBusinessRecord} Created struct
   */
  static fromObject (other) {
    return new JournalBusinessRecord().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!JournalBusinessRecord}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return JournalBusinessRecord.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!JournalBusinessRecord}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 99
  }
}

export { JournalBusinessRecord };

/**
 * Fast Binary Encoding JournalBusinessRecord field model
 */
class FieldModelJournalBusinessRecord extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Time = new fbe.FieldModelOptional(new fbe.FieldModelTimestamp(buffer, 4 + 4), buffer, 4 + 4)
    this.Message = new fbe.FieldModelString(buffer, this.Time.fbeOffset + this.Time.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelJournalBusinessRecord}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelJournalBusinessRecord}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Time.fbeSize + this.Message.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelJournalBusinessRecord}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Time.fbeExtra + this.Message.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelJournalBusinessRecord}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelJournalBusinessRecord.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelJournalBusinessRecord}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 99
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelJournalBusinessRecord}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelJournalBusinessRecord}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Time.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Time.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Time.fbeSize

    if ((fbeCurrentSize + this.Message.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Message.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Message.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelJournalBusinessRecord}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelJournalBusinessRecord}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelJournalBusinessRecord}
   * @param {!JournalBusinessRecord} fbeValue Default value, defaults is new JournalBusinessRecord()
   * @returns {!JournalBusinessRecord} JournalBusinessRecord value
   */
  get (fbeValue = new JournalBusinessRecord()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelJournalBusinessRecord}
   * @param {!JournalBusinessRecord} fbeValue JournalBusinessRecord value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Time.fbeSize) <= fbeStructSize) {
      fbeValue.Time = this.Time.get()
    } else {
      fbeValue.Time = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Time.fbeSize

    if ((fbeCurrentSize + this.Message.fbeSize) <= fbeStructSize) {
      fbeValue.Message = this.Message.get()
    } else {
      fbeValue.Message = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Message.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelJournalBusinessRecord}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelJournalBusinessRecord}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelJournalBusinessRecord}
   * @param {!JournalBusinessRecord} fbeValue JournalBusinessRecord value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelJournalBusinessRecord}
   * @param {JournalBusinessRecord} fbeValue JournalBusinessRecord value
   */
  setFields (fbeValue) {
    this.Time.set(fbeValue.Time)
    this.Message.set(fbeValue.Message)
  }
}

export { FieldModelJournalBusinessRecord };

/**
 * Fast Binary Encoding JournalBusinessRecord model
 */
class JournalBusinessRecordModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelJournalBusinessRecord(this.buffer, 4)
  }

  /**
   * Get the JournalBusinessRecord model
   * @this {!JournalBusinessRecordModel}
   * @returns {!FieldModelJournalBusinessRecord} model JournalBusinessRecord model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!JournalBusinessRecordModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!JournalBusinessRecordModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return JournalBusinessRecordModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!JournalBusinessRecordModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelJournalBusinessRecord.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!JournalBusinessRecordModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!JournalBusinessRecordModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!JournalBusinessRecordModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!JournalBusinessRecordModel}
   * @param {!JournalBusinessRecord} value JournalBusinessRecord value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!JournalBusinessRecordModel}
   * @param {!JournalBusinessRecord} value JournalBusinessRecord value, defaults is new JournalBusinessRecord()
   * @return {!object} Deserialized JournalBusinessRecord value and its size
   */
  deserialize (value = new JournalBusinessRecord()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new JournalBusinessRecord(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new JournalBusinessRecord(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!JournalBusinessRecordModel}
   * @param {!number} prev Previous JournalBusinessRecord model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { JournalBusinessRecordModel };
